import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Grid, Card, CardContent, Button, TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Alert, CircularProgress, FormControlLabel, Checkbox, FormGroup, Select, InputLabel, FormControl, Paper, Chip, Divider, Avatar, Fade, useTheme
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import SportsIcon from '@mui/icons-material/Sports';
// Import API config to set axios defaults
import '../config/api';

const JERSEY_IMAGES = [
  { label: 'Koleksi Jersey RaketerS', src: 'https://i.ibb.co.com/CKDy5K90/kolase-koleksi-jersey.jpg' },
];

const JERSEY_TYPES = [
  { value: 'standar', label: 'Standar' },
  { value: 'lekbong', label: 'Lekbong' },
  { value: 'panjang', label: 'Panjang' },
];

const JERSEY_VERSIONS = [
  { value: 'v1', label: 'Jersey V1.0' },
  { value: 'v2', label: 'Jersey V2.0' },
  { value: 'v3_1', label: 'Jersey V3.1' },
  { value: 'v3_2', label: 'Jersey V3.2' },
  { value: 'v3_3', label: 'Jersey V3.3' },
];

const JERSEY_SIZES = [
  'Anak S', 'Anak M', 'Anak L', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL', '5XL'
];

const SHEET_API = '/api/bookings?action=jersey';

const initialForm = {
  nama: '',
  versi: 'v2',
  tipe: 'standar',
  ukuran: '',
  pakaiNama: false,
  namaPunggung: '',
  kontak: '',
  catatan: '',
};

const JerseyPO: React.FC = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [purchaseId, setPurchaseId] = useState<string | null>(null);
  const [paymentRedirect, setPaymentRedirect] = useState<string | null>(null);
  const [listPO, setListPO] = useState<any[]>([]);
  const [fetchingList, setFetchingList] = useState(false);

  // Fetch list PO dari Google Sheets
  useEffect(() => {
    const fetchList = async () => {
      setFetchingList(true);
      try {
        const res = await axios.get(SHEET_API);
        setListPO(res.data || []);
      } catch {
        setListPO([]);
      } finally {
        setFetchingList(false);
      }
    };
    fetchList();
  }, [success]);

  const handleChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    // Auto-uppercase untuk namaPunggung agar selalu kapital
    let newValue: any = type === 'checkbox' ? checked : value;
    if (name === 'namaPunggung' && typeof newValue === 'string') {
      newValue = newValue.toUpperCase();
    }
    setForm((prev) => ({
      ...prev,
      [name]: newValue,
      ...(name === 'pakaiNama' && !checked ? { namaPunggung: '' } : {})
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);
    try {
      // Validasi
      if (!form.nama || !form.versi || !form.tipe || !form.ukuran || !form.kontak) {
        setError('Semua field wajib diisi!');
        setLoading(false);
        return;
      }
      if (form.pakaiNama && !form.namaPunggung) {
        setError('Nama punggung harus diisi jika memilih pakai nama.');
        setLoading(false);
        return;
      }
      // Submit ke backend dengan format yang sesuai untuk spreadsheet
      const formData = {
        nama: form.nama,
        tipeJersey: form.tipe,
        ukuran: form.ukuran,
        pakaiNamaPunggung: form.pakaiNama ? 'Ya' : 'Tidak',
        namaPunggung: form.namaPunggung || '',
        kontak: form.kontak,
        versiJersey: form.versi,
        sudahBayar: 'Belum',
        idPembelian: ''
      };
      console.log('[JERSEY-PO] Submitting form data:', formData);
      const res = await axios.post(SHEET_API, formData);
      console.log('[JERSEY-PO] Backend response:', res.data);
      const id = res.data?.id;
      if (!id) {
        console.error('[JERSEY-PO] No ID returned from backend');
        setError('Gagal submit order: tidak ada ID yang dikembalikan');
        setLoading(false);
        return;
      }
      setSuccess(true);
      setForm(initialForm);

      // Simpan ID pembelian supaya ditampilkan di dialog
      if (id) setPurchaseId(id);

      // Jika ada id, mulai pembayaran Midtrans dengan harga dinamis berdasarkan ukuran dan tipe
      if (id) {
        try {
          // Use logged-in Google account email if available
          const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
          const email = currentUser?.email || '';
          const price = calculatePrice(form.ukuran, form.tipe);
          const payRes = await axios.post('/api/payment/snap-token', {
            order_id: id,
            gross_amount: price,
            nama: form.nama || 'Pembeli Jersey',
            email
          });
          const redirectUrl = payRes.data?.redirect_url;
          if (redirectUrl) {
            // Simpan redirect URL dan buka otomatis; jika diblokir, user bisa klik tombol di dialog
            setPaymentRedirect(redirectUrl);
            setOpenDialog(true);
            try {
              window.location.href = redirectUrl;
              return;
            } catch (e) {
              // ignore
            }
          }
        } catch (payErr) {
          console.error('Gagal memulai pembayaran Midtrans:', payErr);
          // Tampilkan dialog tanpa redirect, dengan info ID supaya admin bisa proses manual
          setOpenDialog(true);
          return;
        }
      }

      setOpenDialog(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Gagal submit, coba lagi.');
    } finally {
      setLoading(false);
    }
  };

  // Filter only paid orders (kolom H berisi 'Sudah')
  const paidList = listPO.filter(row => (String(row.sudahBayar || '').toLowerCase()).includes('sudah'));

  // Calculate price based on size and type
  const calculatePrice = (size: string, type: string = 'standar'): number => {
    const basePrice = 130000;
    const xlIndex = JERSEY_SIZES.indexOf('XL');
    const selectedIndex = JERSEY_SIZES.indexOf(size);
    
    let price = basePrice;
    
    // Add 5k for each size above XL
    if (selectedIndex > xlIndex) {
      const extraSizes = selectedIndex - xlIndex;
      price += (extraSizes * 5000);
    }
    
    // Add 15k for panjang type
    if (type === 'panjang') {
      price += 15000;
    }
    
    return price;
  };

  const currentPrice = form.ukuran && form.tipe ? calculatePrice(form.ukuran, form.tipe) : 0;

  return (
    <>
      <Helmet>
        <title>PO Jersey RaketerS</title>
      </Helmet>
      
      {/* Hero Section */}
      <Box
        sx={{
          background: isDarkMode 
            ? 'linear-gradient(135deg, #0d47a1 0%, #1565c0 50%, #1976d2 100%)'
            : 'linear-gradient(135deg, #1976d2 0%, #42a5f5 50%, #64b5f6 100%)',
          py: 8,
          px: 2,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: isDarkMode
              ? 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.05) 0%, transparent 50%)'
              : 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)',
            pointerEvents: 'none'
          }
        }}
      >
        <Fade in timeout={1000}>
          <Box>
            <Typography 
              variant="h3" 
              fontWeight={800} 
              align="center" 
              gutterBottom
              sx={{
                color: 'white',
                textShadow: '0 4px 8px rgba(0,0,0,0.2)',
                mb: 2
              }}
            >
              Pre-Order Jersey RaketerS
            </Typography>
            <Typography 
              variant="h6" 
              align="center"
              sx={{
                color: 'rgba(255,255,255,0.95)',
                mb: 4,
                fontWeight: 500
              }}
            >
              Koleksi Jersey Premium untuk Komunitas Badminton
            </Typography>
            
            {/* Timeline Chips */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', mb: 4 }}>
              <Chip
                icon={<CheckCircleIcon />}
                label="Preorder: 25 Oktober-5 November 2026"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 600,
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.3)'
                }}
              />
              <Chip
                icon={<SportsIcon />}
                label="Pengerjaan: 6-19 November 2026"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 600,
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.3)'
                }}
              />
              <Chip
                icon={<LocalShippingIcon />}
                label="Pengambilan: 20 November 2026"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 600,
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.3)'
                }}
              />
            </Box>

            <Typography 
              variant="body1" 
              align="center"
              sx={{
                color: 'rgba(255,255,255,0.9)',
                fontStyle: 'italic'
              }}
            >
              Bisa dipaketkan atau kirim instan
            </Typography>
          </Box>
        </Fade>
      </Box>

      {/* Jersey Image Section */}
      <Container maxWidth={false} disableGutters sx={{ py: 4, bgcolor: isDarkMode ? '#1a1a2e' : '#f5f5f5' }}>
        <Fade in timeout={1200}>
          <Box sx={{ textAlign: 'center', mx: -2 }}>
            <Box
              sx={{
                position: 'relative',
                display: 'inline-block',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -10,
                  left: -10,
                  right: -10,
                  bottom: -10,
                  background: isDarkMode 
                    ? 'linear-gradient(45deg, #0d47a1, #1565c0, #1976d2)'
                    : 'linear-gradient(45deg, #1976d2, #42a5f5, #64b5f6)',
                  borderRadius: '20px',
                  zIndex: 0,
                  opacity: isDarkMode ? 0.15 : 0.1
                }
              }}
            >
              <img
                src={JERSEY_IMAGES[0].src}
                alt={JERSEY_IMAGES[0].label}
                loading="lazy"
                style={{
                  width: '50vw',
                  height: 'auto',
                  maxWidth: 'none',
                  display: 'block',
                  marginLeft: 'calc(-25vw + 50%)',
                  marginRight: 'calc(-25vw + 50%)',
                  position: 'relative',
                  zIndex: 1,
                  borderRadius: '12px',
                  boxShadow: isDarkMode 
                    ? '0 20px 60px rgba(25, 118, 210, 0.5)'
                    : '0 20px 60px rgba(25, 118, 210, 0.3)'
                }}
              />
            </Box>
            <Typography 
              align="center" 
              fontWeight={700} 
              sx={{ 
                mt: 3,
                fontSize: '1.2rem',
                color: isDarkMode ? '#64b5f6' : '#1976d2'
              }}
            >
              {JERSEY_IMAGES[0].label}
            </Typography>
          </Box>
        </Fade>
      </Container>
      <Container maxWidth="lg" sx={{ py: 6 }}>
        
        {/* Specifications Section */}
        <Paper 
          elevation={3}
          sx={{ 
            p: 4, 
            mb: 4,
            borderRadius: 3,
            background: isDarkMode 
              ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
            border: isDarkMode 
              ? '1px solid rgba(100, 181, 246, 0.2)'
              : '1px solid rgba(25, 118, 210, 0.1)'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <WorkspacePremiumIcon sx={{ fontSize: 32, color: isDarkMode ? '#64b5f6' : '#1976d2', mr: 2 }} />
            <Typography variant="h5" fontWeight={700} sx={{ color: isDarkMode ? '#64b5f6' : '#1976d2' }}>
              Spesifikasi Jersey
            </Typography>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ 
                p: 2, 
                bgcolor: isDarkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.05)', 
                borderRadius: 2,
                textAlign: 'center'
              }}>
                <Typography variant="h6" fontWeight={700} sx={{ color: isDarkMode ? '#64b5f6' : '#1976d2' }}>
                  Bahan Premium
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  Jacquard berkualitas tinggi
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ 
                p: 2, 
                bgcolor: isDarkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.05)', 
                borderRadius: 2,
                textAlign: 'center'
              }}>
                <Typography variant="h6" fontWeight={700} sx={{ color: isDarkMode ? '#64b5f6' : '#1976d2' }}>
                  Harga Terjangkau
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  Mulai Rp130.000
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ 
                p: 2, 
                bgcolor: isDarkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.05)', 
                borderRadius: 2,
                textAlign: 'center'
              }}>
                <Typography variant="h6" fontWeight={700} sx={{ color: isDarkMode ? '#64b5f6' : '#1976d2' }}>
                  Nyaman Dipakai
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  Menyerap keringat dengan baik
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Divider sx={{ my: 3, borderColor: isDarkMode ? 'rgba(100, 181, 246, 0.2)' : 'rgba(0,0,0,0.1)' }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                <Typography variant="body1">Tersedia ukuran anak-anak</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                <Typography variant="body1">Bisa request nama punggung</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                <Typography variant="body1">3 tipe jersey tersedia</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                <Typography variant="body1">5 versi koleksi jersey</Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
        
        {/* Size Chart Section */}
        <Paper 
          elevation={3}
          sx={{ 
            p: 4, 
            mb: 4,
            borderRadius: 3,
            background: isDarkMode 
              ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
            border: isDarkMode 
              ? '1px solid rgba(100, 181, 246, 0.2)'
              : '1px solid rgba(25, 118, 210, 0.1)'
          }}
        >
          <Typography variant="h5" fontWeight={700} gutterBottom sx={{ color: isDarkMode ? '#64b5f6' : '#1976d2', mb: 3 }}>
            Size Chart
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" fontWeight={600} sx={{ color: isDarkMode ? '#64b5f6' : '#1976d2', mb: 2, textAlign: 'center' }}>
                Size Chart Anak-Anak
              </Typography>
              <Box sx={{ textAlign: 'center' }}>
                <img 
                  src="https://i.ibb.co.com/zWLyvmyQ/Cuplikan-layar-2026-07-11-191935.png" 
                  alt="Size Chart Anak-Anak" 
                  style={{ 
                    width: '100%', 
                    maxWidth: '400px', 
                    height: 'auto',
                    borderRadius: '12px',
                    boxShadow: isDarkMode 
                      ? '0 8px 24px rgba(100, 181, 246, 0.3)'
                      : '0 8px 24px rgba(0,0,0,0.15)'
                  }} 
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" fontWeight={600} sx={{ color: isDarkMode ? '#64b5f6' : '#1976d2', mb: 2, textAlign: 'center' }}>
                Size Chart Dewasa
              </Typography>
              <Box sx={{ textAlign: 'center' }}>
                <img 
                  src="https://i.ibb.co.com/mFvPBCCN/Cuplikan-layar-2026-07-11-191946.png" 
                  alt="Size Chart Dewasa" 
                  style={{ 
                    width: '100%', 
                    maxWidth: '400px', 
                    height: 'auto',
                    borderRadius: '12px',
                    boxShadow: isDarkMode 
                      ? '0 8px 24px rgba(100, 181, 246, 0.3)'
                      : '0 8px 24px rgba(0,0,0,0.15)'
                  }} 
                />
              </Box>
            </Grid>
          </Grid>
        </Paper>
        
        {/* Order Form Section */}
        <Paper 
          elevation={3}
          sx={{ 
            p: 4, 
            mb: 4,
            borderRadius: 3,
            background: isDarkMode 
              ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
            border: isDarkMode 
              ? '1px solid rgba(100, 181, 246, 0.2)'
              : '1px solid rgba(25, 118, 210, 0.1)'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <SportsIcon sx={{ fontSize: 32, color: isDarkMode ? '#64b5f6' : '#1976d2', mr: 2 }} />
            <Typography variant="h5" fontWeight={700} sx={{ color: isDarkMode ? '#64b5f6' : '#1976d2' }}>
              Formulir Pemesanan Jersey
            </Typography>
          </Box>
          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 3 }}>Pemesanan berhasil! Silakan lakukan pembayaran.</Alert>}
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Nama Pemesan"
                  name="nama"
                  value={form.nama}
                  onChange={handleChange}
                  fullWidth
                  required
                  variant="outlined"
                  sx={{ bgcolor: isDarkMode ? '#1a1a2e' : 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Kontak (WA/HP)"
                  name="kontak"
                  value={form.kontak}
                  onChange={handleChange}
                  fullWidth
                  required
                  variant="outlined"
                  sx={{ bgcolor: isDarkMode ? '#1a1a2e' : 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required variant="outlined" sx={{ bgcolor: isDarkMode ? '#1a1a2e' : 'white' }}>
                  <InputLabel id="versi-jersey-label">Versi Jersey</InputLabel>
                  <Select
                    labelId="versi-jersey-label"
                    name="versi"
                    value={form.versi}
                    label="Versi Jersey"
                    onChange={handleChange}
                    required
                  >
                    {JERSEY_VERSIONS.map((v) => (
                      <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required variant="outlined" sx={{ bgcolor: isDarkMode ? '#1a1a2e' : 'white' }}>
                  <InputLabel id="tipe-jersey-label">Tipe Jersey</InputLabel>
                  <Select
                    labelId="tipe-jersey-label"
                    name="tipe"
                    value={form.tipe}
                    label="Tipe Jersey"
                    onChange={handleChange}
                    required
                  >
                    {JERSEY_TYPES.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.label} {t.value === 'panjang' ? '(+Rp15.000)' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required variant="outlined" sx={{ bgcolor: isDarkMode ? '#1a1a2e' : 'white' }}>
                  <InputLabel id="ukuran-jersey-label">Ukuran</InputLabel>
                  <Select
                    labelId="ukuran-jersey-label"
                    name="ukuran"
                    value={form.ukuran}
                    label="Ukuran"
                    onChange={handleChange}
                    required
                  >
                    {JERSEY_SIZES.map((s) => (
                      <MenuItem key={s} value={s}>{s} - Rp{calculatePrice(s, form.tipe).toLocaleString('id-ID')}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {form.ukuran && form.tipe && (
                <Grid item xs={12}>
                  <Alert 
                    severity="info" 
                    sx={{ 
                      mb: 3,
                      bgcolor: isDarkMode ? 'rgba(100, 181, 246, 0.15)' : 'rgba(25, 118, 210, 0.1)',
                      '& .MuiAlert-icon': {
                        color: isDarkMode ? '#64b5f6' : '#1976d2'
                      }
                    }}
                  >
                    Harga untuk ukuran <strong>{form.ukuran}</strong> dan tipe <strong>{form.tipe === 'panjang' ? 'Panjang' : form.tipe === 'lekbong' ? 'Lekbong' : 'Standar'}</strong>: <strong>Rp{currentPrice.toLocaleString('id-ID')}</strong>
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={form.pakaiNama} 
                        onChange={handleChange} 
                        name="pakaiNama"
                        sx={{ color: isDarkMode ? '#64b5f6' : '#1976d2', '&.Mui-checked': { color: isDarkMode ? '#64b5f6' : '#1976d2' } }}
                      />
                    }
                    label="Pakai Nama Punggung?"
                  />
                </FormGroup>
              </Grid>
              {form.pakaiNama && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Nama Punggung"
                    name="namaPunggung"
                    value={form.namaPunggung}
                    onChange={handleChange}
                    fullWidth
                    required={form.pakaiNama}
                    variant="outlined"
                    sx={{ bgcolor: isDarkMode ? '#1a1a2e' : 'white' }}
                    helperText="Akan otomatis diubah menjadi huruf kapital"
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  label="Catatan (opsional)"
                  name="catatan"
                  value={form.catatan}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  minRows={3}
                  variant="outlined"
                  sx={{ bgcolor: isDarkMode ? '#1a1a2e' : 'white' }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button 
                  type="submit" 
                  variant="contained" 
                  fullWidth 
                  disabled={loading} 
                  sx={{ 
                    py: 2, 
                    fontWeight: 700, 
                    fontSize: 18,
                    bgcolor: isDarkMode ? '#64b5f6' : '#1976d2',
                    '&:hover': {
                      bgcolor: isDarkMode ? '#42a5f5' : '#1565c0'
                    },
                    borderRadius: 2
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit PO Jersey'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      {/* List PO Section */}
      <Paper 
        elevation={3}
        sx={{ 
          p: 4, 
          mb: 4,
          borderRadius: 3,
          background: isDarkMode 
            ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
            : 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
          border: isDarkMode 
            ? '1px solid rgba(100, 181, 246, 0.2)'
            : '1px solid rgba(25, 118, 210, 0.1)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <CheckCircleIcon sx={{ fontSize: 32, color: isDarkMode ? '#64b5f6' : '#1976d2', mr: 2 }} />
          <Typography variant="h5" fontWeight={700} sx={{ color: isDarkMode ? '#64b5f6' : '#1976d2' }}>
            List Preorder Jersey
          </Typography>
        </Box>
        {fetchingList ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {paidList.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, bgcolor: isDarkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.05)', borderRadius: 2 }}>
                <Typography variant="body1" color="text.secondary">
                  Belum ada pemesan yang sudah membayar
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {paidList.map((row, idx) => {
                  const versiLabel = row.versi === 'v1' ? 'Jersey V1.0' : row.versi === 'v2' ? 'Jersey V2.0' : row.versi === 'v3_1' ? 'Jersey V3.1' : row.versi === 'v3_2' ? 'Jersey V3.2' : row.versi === 'v3_3' ? 'Jersey V3.3' : (row.versi || 'Jersey V2.0');
                  return (
                    <Grid item xs={12} sm={6} md={4} key={idx}>
                      <Card 
                        elevation={2}
                        sx={{
                          height: '100%',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: isDarkMode 
                              ? '0 8px 16px rgba(100, 181, 246, 0.3)'
                              : '0 8px 16px rgba(25, 118, 210, 0.2)'
                          },
                          border: isDarkMode 
                            ? '1px solid rgba(100, 181, 246, 0.2)'
                            : '1px solid rgba(25, 118, 210, 0.1)',
                          bgcolor: isDarkMode ? '#1a1a2e' : 'background.paper'
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Avatar 
                              sx={{ 
                                bgcolor: isDarkMode ? '#64b5f6' : '#1976d2', 
                                width: 32, 
                                height: 32,
                                fontSize: 14,
                                fontWeight: 700
                              }}
                            >
                              {idx + 1}
                            </Avatar>
                            <Typography variant="h6" fontWeight={700} sx={{ ml: 2, color: isDarkMode ? '#64b5f6' : '#1976d2' }}>
                              {row.nama}
                            </Typography>
                          </Box>
                          <Divider sx={{ mb: 2, borderColor: isDarkMode ? 'rgba(100, 181, 246, 0.2)' : 'rgba(0,0,0,0.1)' }} />
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 60 }}>
                                Versi:
                              </Typography>
                              <Chip 
                                label={versiLabel} 
                                size="small" 
                                sx={{ 
                                  bgcolor: isDarkMode ? 'rgba(100, 181, 246, 0.15)' : 'rgba(25, 118, 210, 0.1)',
                                  color: isDarkMode ? '#64b5f6' : '#1976d2',
                                  fontWeight: 600
                                }} 
                              />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 60 }}>
                                Tipe:
                              </Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {row.tipe}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 60 }}>
                                Ukuran:
                              </Typography>
                              <Chip 
                                label={row.ukuran} 
                                size="small" 
                                variant="outlined"
                                sx={{ 
                                  borderColor: isDarkMode ? '#64b5f6' : '#1976d2',
                                  color: isDarkMode ? '#64b5f6' : '#1976d2',
                                  fontWeight: 600
                                }} 
                              />
                            </Box>
                            {row.pakaiNama && row.namaPunggung && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 60 }}>
                                  Nama:
                                </Typography>
                                <Typography variant="body2" fontWeight={700} sx={{ color: isDarkMode ? '#64b5f6' : '#1976d2' }}>
                                  {row.namaPunggung}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  )
                })}
              </Grid>
            )}
          </>
        )}
      </Paper>
      <Dialog 
        open={openDialog} 
        onClose={() => { setOpenDialog(false); setPurchaseId(null); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: isDarkMode ? '#1a1a2e' : 'background.paper'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: isDarkMode 
            ? 'linear-gradient(135deg, #0d47a1 0%, #1565c0 100%)'
            : 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
          fontWeight: 700
        }}>
          Pembayaran Jersey RaketerS
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {purchaseId ? (
            <>
              <Alert severity="info" sx={{ mb: 3, bgcolor: isDarkMode ? 'rgba(100, 181, 246, 0.15)' : 'rgba(25, 118, 210, 0.1)' }}>
                <Typography variant="body1">
                  ID Pembelian Anda: <strong>{purchaseId}</strong>
                </Typography>
              </Alert>
              <Typography gutterBottom sx={{ mb: 2 }}>
                Jika pembayaran tidak otomatis diarahkan, silakan klik tombol di bawah.
              </Typography>
              {paymentRedirect && (
                <Box sx={{ textAlign: 'center', mt: 2, mb: 3 }}>
                  <Button 
                    variant="contained" 
                    onClick={() => window.open(paymentRedirect, '_blank')}
                    sx={{
                      bgcolor: isDarkMode ? '#64b5f6' : '#1976d2',
                      '&:hover': {
                        bgcolor: isDarkMode ? '#42a5f5' : '#1565c0'
                      },
                      py: 1.5,
                      px: 3
                    }}
                  >
                    Lanjut ke Pembayaran
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <Alert severity="info" sx={{ mb: 3 }}>
              Instruksi pembayaran akan muncul setelah proses pembayaran dimulai.
            </Alert>
          )}
          <Box sx={{ textAlign: 'center', my: 3 }}>
            <img 
              src="/images/qr_code_payment.png" 
              alt="QRIS RaketerS" 
              style={{ 
                maxWidth: 280, 
                borderRadius: 12, 
                boxShadow: isDarkMode 
                  ? '0 8px 24px rgba(100, 181, 246, 0.3)'
                  : '0 8px 24px rgba(0,0,0,0.15)' 
              }} 
            />
          </Box>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ fontStyle: 'italic' }}>
            Setelah transfer, konfirmasi ke admin atau kirim bukti via WhatsApp RaketerS.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: isDarkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.05)' }}>
          <Button 
            onClick={() => { setOpenDialog(false); setPurchaseId(null); }} 
            variant="contained"
            sx={{
              bgcolor: isDarkMode ? '#64b5f6' : '#1976d2',
              '&:hover': {
                bgcolor: isDarkMode ? '#42a5f5' : '#1565c0'
              }
            }}
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
    </>
  );
};

export default JerseyPO;
